import React, { CSSProperties } from 'react';
import { InboxOutlined, DownloadOutlined } from '@ant-design/icons';
import Upload, { RcFile } from 'antd/lib/upload';
import { useTranslation } from 'react-i18next';
import { Button, Card, Col, Divider, message, Modal, Row, Spin } from 'antd';
import { ModalWidth, ROW_GUTTER } from '@/config';
import { fileService } from '@/services/fileService';
import { BaseImportResponse, BaseImportColumn } from '@/__generated';

const { Dragger } = Upload;

interface Props {
  onImport: (file: RcFile) => Promise<BaseImportResponse>;
  onClose: (reload: boolean) => void;
  cols: BaseImportColumn[];
  sampleDownloadUrl?: string;
}
function ImportFileModal({ onImport, onClose, cols, sampleDownloadUrl }: Props) {
  const { t } = useTranslation();
  const [loading, setLoading] = React.useState<boolean>(false);
  const [uploadResponse, setUploadResponse] = React.useState<BaseImportResponse>();
  const [needReload, setNeedReload] = React.useState<boolean>(false);
  const onUpload = ({ file, onError, onSuccess }: any) => {
    setLoading(true);
    onImport(file)
      .then(res => {
        if (res.success) {
          setNeedReload(true);
          setUploadResponse(res);
          onSuccess();
        } else {
          onError(t(res.message || ''));
          message.error(t(res.message || ''));
          setUploadResponse(undefined);
        }
      })
      .catch(err => {
        onError(err);
        setUploadResponse(undefined);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  return (
    <Modal
      style={{ top: 10 }}
      open={true}
      width={ModalWidth.large}
      className="nta-custom-form"
      title={t('Import')}
      onCancel={() => {
        onClose(needReload);
      }}
      cancelText={t('Close')}
      okButtonProps={{ style: { display: 'none' } }}
    >
      <Spin spinning={loading}>
        <Card title={t('Note')} className="mtopo-card" size="small">
          <div dangerouslySetInnerHTML={{ __html: t('importModal_HintText') }}></div>
          {sampleDownloadUrl ? (
            <b>
              <a href={sampleDownloadUrl} style={{ color: '#118ABE' }}>
                {t('importModal_DownloadSampleFle')}
              </a>
            </b>
          ) : null}
        </Card>
        <Divider />
        <Card title={t('importModal_ColumnsHintText')} className="mtopo-card" size="small">
          <div style={style.colsContainer}>
            {cols.map(col => {
              return (
                <div key={col.columnName} style={style.colItem}>
                  {col.columnName}
                  {col.isRequired ? <span style={{ color: 'red' }}>*</span> : null}
                </div>
              );
            })}
          </div>
        </Card>

        <Divider />
        <Row gutter={ROW_GUTTER} style={{ marginBottom: 20 }}>
          <Col md={8}>
            <Card title={t('importModal_ImportResponse')} className="mtopo-card" size="small">
              {uploadResponse ? (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    {t('importModal_InsertedRecords')} <b>{uploadResponse.insertedRecords}</b>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    {t('importModal_InvalidRecords')} <b>{uploadResponse.invalidRecords}</b>
                  </div>
                  {uploadResponse.logUrl ? (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      {t('importModal_LogUrl')}
                      <Button
                        style={{ paddingRight: 0 }}
                        type="link"
                        icon={<DownloadOutlined />}
                        onClick={() => {
                          fileService.downloadProtected(uploadResponse.logUrl || '', 'import-log.csv');
                        }}
                      >
                        {t('Download')}
                      </Button>
                    </div>
                  ) : null}

                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    {t('importModal_totalRecords')} <b>{uploadResponse.totalRecords}</b>
                  </div>
                </>
              ) : null}
            </Card>
          </Col>
          <Col md={16}>
            <Dragger customRequest={onUpload} multiple={false} maxCount={1} accept=".xlsx">
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">{t('importModal_ClickOrDrag2Upload')}</p>
            </Dragger>
          </Col>
        </Row>
      </Spin>
    </Modal>
  );
}
const style: { [key: string]: CSSProperties } = {
  colsContainer: {
    display: 'flex',
  },
  colItem: {
    flex: 1,
    padding: '5px 10px',
  },
};
export default ImportFileModal;
